import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { api, errText } from "@/lib/api";
import { trackEvent } from "@/lib/gtag";

export const EnquiryForm = ({ propertyId, compact = false, source = "", submitLabel = "Send enquiry" }) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "", visited: false, visit_date: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Name and phone number are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/enquiries", { ...form, property_id: propertyId || null, source });
      trackEvent("generate_lead", { event_category: "enquiry", property_id: propertyId || "general", source: source || "site" });
      toast.success("Enquiry sent. Our team will call you shortly.");
      setForm({ name: "", phone: "", email: "", message: "", visited: false, visit_date: "" });
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3.5" data-testid="enquiry-form">
      <div className="grid gap-3.5 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">Full name</Label>
          <Input value={form.name} onChange={set("name")} className="h-11 rounded-lg" data-testid="enquiry-name-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">Phone</Label>
          <Input value={form.phone} onChange={set("phone")} className="h-11 rounded-lg" data-testid="enquiry-phone-input" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs font-semibold">Email (optional)</Label>
        <Input value={form.email} onChange={set("email")} className="h-11 rounded-lg" data-testid="enquiry-email-input" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs font-semibold">Message</Label>
        <Textarea
          value={form.message}
          onChange={set("message")}
          rows={compact ? 2 : 3}
          className="rounded-lg"
          data-testid="enquiry-message-input"
        />
      </div>
      <div className="flex items-center gap-3 pt-1">
        <Checkbox
          id={`visited-${propertyId || "general"}`}
          checked={form.visited}
          onCheckedChange={(v) => setForm({ ...form, visited: !!v })}
          data-testid="enquiry-visited-checkbox"
        />
        <Label htmlFor={`visited-${propertyId || "general"}`} className="text-xs cursor-pointer font-medium text-muted-foreground">
          I have visited / want to schedule a site visit
        </Label>
      </div>
      {form.visited && (
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">Visit date</Label>
          <Input type="date" value={form.visit_date} onChange={set("visit_date")} className="h-11 rounded-lg" data-testid="enquiry-visit-date-input" />
        </div>
      )}
      <Button
        type="submit"
        disabled={busy}
        className="w-full h-12 rounded-lg text-[15px] font-bold hover:brightness-95"
        data-testid="enquiry-submit-btn"
      >
        {busy ? "Sending…" : submitLabel}
      </Button>
      <p className="text-center text-[11px] text-muted-foreground">You won't be charged anything</p>
    </form>
  );
};

export default EnquiryForm;
